import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    useTheme,
} from '@mui/material'
import { useState } from 'react'
import { useLoginScreenStyle } from '../hooks/useLoginScreenStyleHook'
import LoginInputField from '../LoginInputField'
import { useTranslation } from 'react-i18next'
import useUserInputHook from '../hooks/useUserInputHook'
import InputErrorPopUp from '../InputErrorPopUp'
import { RawLink } from '@/shared/ui/RawLink'
import { useAppStore } from '@/shared/store'
import { TermsAndConditionsPopUp } from '@/features/termsAndConditions/ui/TermsAndConditionsPopUp'

interface Props {
    handleLogInClick: (shouldOpenDeviceManager?: boolean) => void
    handleGuestClick: () => void
}

const LogInMenu = ({ handleLogInClick, handleGuestClick }: Props) => {
    const {
        underLineButtonStyle,
        colorButtonStyle,
        colorButtonGuestStyle,
        subtitle2Style,
    } = useLoginScreenStyle()

    const {
        username,
        password,
        inputErrors,
        checkPasswordChange,
        checkUsernameChange,
        logIn,
        isError,
    } = useUserInputHook(handleLogInClick)

    const { t: translate } = useTranslation()
    const theme = useTheme()

    const { termsAndConditions, setTermsAndConditions } = useAppStore()
    const [isTermsPopUpOpen, setIsTermsPopUpOpen] = useState(false)

    const isTermsAccepted = Boolean(termsAndConditions?.isTermsAccepted)
    const isTermsBlocking = !isTermsAccepted

    const handleTermsToggle = () =>
        void setTermsAndConditions({
            ...termsAndConditions,
            isTermsAccepted: !isTermsAccepted,
        })

    const loginMenuContainer = {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        minWidth: '350px',
        alignItems: 'center',
        height: '100%',
        marginBottom: { xs: '40px', md: 'unset' },
    }

    const buttonContainerStyles = {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        marginTop: '20px',
        position: 'relative',
    }

    const inputContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        position: 'relative',
    }

    const inputStyle = {
        padding: '8px',
        fontSize: '16px',
        borderRadius: '25px',
        color: theme.palette.white,
        border: `1px solid ${theme.palette.white}`,
        '&:focus-within': {
            border: `1px solid ${theme.palette.primary.main}`,
        },
    }

    const buttonUnderLineContainerStyles = {
        display: 'flex',
        marginTop: '20px',
        gap: '5rem',
    }

    return (
        <Box sx={loginMenuContainer}>
            <Typography sx={subtitle2Style}>
                {translate('existingSubscriberMessage')}
            </Typography>
            <>
                <Box sx={inputContainerStyle}>
                    <LoginInputField
                        placeholder={translate('emailLoginHint')}
                        inputValue={username}
                        setInputValue={checkUsernameChange}
                        styles={inputStyle}
                        native={{ type: 'email', name: 'email' }}
                        defaultValue={''}
                    />
                    <InputErrorPopUp
                        styles={{
                            position: 'absolute',
                            top: '50px',
                            width: 'max-content',
                        }}
                        errorMessage={translate(inputErrors.usernameError)}
                    />
                </Box>

                <Box sx={inputContainerStyle}>
                    <LoginInputField
                        placeholder={translate('passwordLoginHint')}
                        inputValue={password}
                        defaultValue={''}
                        setInputValue={checkPasswordChange}
                        styles={inputStyle}
                        native={{ type: 'password', name: 'password' }}
                    />
                    <InputErrorPopUp
                        styles={{
                            position: 'absolute',
                            top: '50px',
                            width: 'max-content',
                        }}
                        errorMessage={translate(inputErrors.passwordError)}
                    />
                </Box>
            </>
            <FormControlLabel
                sx={{ marginTop: 0, marginRight: 0, marginBottom: '-5px' }}
                control={
                    <Checkbox
                        data-testid="accept-terms-checkbox"
                        checked={isTermsAccepted}
                        onChange={handleTermsToggle}
                        name="acceptTerms"
                        sx={{
                            color: theme.palette.white,
                            '&.Mui-checked': {
                                color: theme.palette.primary.main,
                            },
                        }}
                    />
                }
                label={
                    <Typography sx={{ color: theme.palette.white }}>
                        {translate('acceptTermsPrefix')}{' '}
                        <Box
                            component="span"
                            data-testid="open-terms-link"
                            onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                setIsTermsPopUpOpen(true)
                            }}
                            sx={{
                                color: theme.palette.primary.main,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                            }}>
                            {translate('termsLinkWord')}
                        </Box>
                    </Typography>
                }
            />

            <Box sx={buttonContainerStyles}>
                <InputErrorPopUp
                    styles={{
                        position: 'absolute',
                        top: '-30px',
                        width: 'max-content',
                    }}
                    errorMessage={translate(inputErrors.dataValidationError)}
                />

                <Button
                    sx={colorButtonStyle}
                    onClick={logIn}
                    disabled={isError || isTermsBlocking}>
                    {translate('loginButtonText')}
                </Button>

                <Button
                    sx={colorButtonGuestStyle}
                    onClick={handleGuestClick}
                    disabled={isTermsBlocking}>
                    {translate('continueAsGuestButtonText')}
                </Button>
            </Box>

            <TermsAndConditionsPopUp
                isOpen={isTermsPopUpOpen}
                onClose={() => void setIsTermsPopUpOpen(false)}
            />
            <Box sx={buttonUnderLineContainerStyles}>
                <RawLink to="https://eboa.toya.net.pl/auth/reset-password-2">
                    <Button
                        sx={underLineButtonStyle}
                        disableRipple
                        style={{ marginTop: 0 }}>
                        {translate('forgotPasswordQuestion')}
                    </Button>
                </RawLink>
                <RawLink to="https://eboa.toya.net.pl/auth/register">
                    <Button
                        sx={underLineButtonStyle}
                        disableRipple
                        style={{ marginTop: 0 }}>
                        {translate('dontHaveAccountQuestion')}
                    </Button>
                </RawLink>
            </Box>
        </Box>
    )
}

export default LogInMenu
